/** @jsxImportSource @emotion/react */
import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { Text } from "@phoenix/components";
import type { TracesSummaryQuery } from "./__generated__/TracesSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const bigNumberContainerCSS = css`
  margin-bottom: 8px;
`;

const bigNumberCSS = css`
  font-size: 32px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  margin-right: 8px;
`;

const subtitleCSS = css`
  font-size: 14px;
  color: var(--ac-global-text-color-500);
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 0;
  
  /* Machi hover effect for recharts bars */
  .recharts-bar-rectangle path {
    transition: all 0.3s ease-in-out;
    cursor: pointer;
  }
  .recharts-bar-rectangle path:hover {
    filter: brightness(1.15) drop-shadow(0px 0px 4px rgba(0,0,0,0.2));
  }
`;

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4"];

export function TracesSummary({ projectId, timeRange }: ProjectMetricViewProps) {
  const data = useLazyLoadQuery<TracesSummaryQuery>(
    graphql`
      query TracesSummaryQuery($projectId: ID!, $timeRange: TimeRange!) {
        project: node(id: $projectId) {
          ... on Project {
            # Fetch spans for breakdown visualization - increased for large time ranges machi!
            spans(timeRange: $timeRange, rootSpansOnly: true, first: 10000) {
              edges {
                span: node {
                  agentName
                }
              }
            }
          }
        }
      }
    `,
    {
      projectId,
      timeRange: {
        start: timeRange.start?.toISOString(),
        end: timeRange.end?.toISOString(),
      },
    }
  );

  const { chartData, totalTraces } = useMemo(() => {
    const edges = data.project?.spans?.edges ?? [];
    const total = edges.length;
    const map: Record<string, number> = {};
    for (const { span } of edges) {
      const agentName = span.agentName ?? "Unknown";
      map[agentName] = (map[agentName] ?? 0) + 1;
    }
    const sorted = Object.entries(map)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    return { chartData: sorted, totalTraces: total };
  }, [data]);

  return (
    <div css={containerCSS}>
      <div css={bigNumberContainerCSS}>
        <span css={bigNumberCSS}>{totalTraces}</span>
        <span css={subtitleCSS}>Total traces tracked</span>
      </div>
      <div css={chartContainerCSS}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
            <defs>
              <linearGradient id="colorTraces" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4338ca" stopOpacity={0.85} />
                <stop offset="95%" stopColor="#4338ca" stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fill: "var(--ac-global-text-color-500)" }}
              angle={-25}
              textAnchor="end"
              interval={0}
              height={60}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 13, fill: "var(--ac-global-text-color-500)" }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--ac-global-background-color-dark)",
                borderColor: "var(--ac-global-color-grey-100)",
                borderRadius: "8px",
                color: "var(--ac-global-text-color-900)"
              }}
              cursor={{ stroke: "#4338ca", strokeWidth: 1, strokeDasharray: "4 4", fill: "transparent" }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#4338ca"
              strokeWidth={2.5}
              fill="url(#colorTraces)"
              dot={{ fill: "#eab308", stroke: "#fff", strokeWidth: 1.5, r: 4 }}
              activeDot={{ r: 6, fill: "#4338ca", stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
